/**
 * Request builders (getFilters + §10.6 buildRequest). Pure
 * reads of the shared atoms - no DOM, no network. The estimate scheduler and
 * (once ported) the export/preview flows all funnel through `buildExportRequest`
 * so the request shape is defined in exactly one place.
 */

import {
  activeDataSource,
  selectedShip,
  startDate,
  endDate,
  selectedCols,
  allColumns,
  filters,
  filterLogic,
  filterExpr,
  filterMode,
  exportFormat,
  fillMethod,
  frequencyValue,
  frequencyUnit,
  derivedSignals,
  outliers,
  type DerivedSignal,
  type FilterMode,
  type FilterRow,
  type OutlierState,
} from './store.ts';
import { isNumericType, isTemporalType } from '../utils/columns.ts';
import { sourceField } from './data-sources.ts';
import { validateExpr, type ExprKind } from '../features/filters/expr-parser.ts';
import type {
  ExportRequest,
  FilterCondition,
  FilterGroup,
  OutlierSpecReq,
  DerivedSignalReq,
} from '../api/types.ts';

/**
 * Explicit inputs for the three shaping funnels, replacing the shared atoms.
 *
 * Every field is optional and falls back to its atom, so Explore keeps calling
 * these with no argument and gets exactly today's behavior. A dashboard tile
 * resolves these from its own query string instead - the tile is a saved recipe,
 * and it must send the filters it was saved with rather than whatever the
 * sidebar happens to hold now.
 */
export interface ShapingSource {
  filterRows?: FilterRow[];
  logic?: 'AND' | 'OR';
  expr?: string;
  mode?: FilterMode;
  derived?: DerivedSignal[];
  outliers?: OutlierState;
  /**
   * Dashboard slicer selections: each keeps only the readings whose column is
   * one of `values`, or lies within `min`..`max` (epoch seconds when `temporal`).
   */
  slicers?: Array<{
    column: string;
    values?: string[];
    min?: number | null;
    max?: number | null;
    temporal?: boolean;
  }>;
}

/**
 * Resolve a name to its expression kind for the client-side validator: derived
 * signals are always numeric, raw columns map by declared type, unknown names
 * return `undefined` (so the validator flags them). Mirrors the backend.
 */
export function exprKindOf(name: string): ExprKind | undefined {
  if (derivedSignals.value.some((d) => d.name === name)) return 'num';
  const col = allColumns.value.find((c) => c.name === name);
  if (!col) return undefined;
  if (isTemporalType(col.type)) return 'temporal';
  if (isNumericType(col.type)) return 'num';
  return 'str';
}

/**
 * The experimental filter expression to send, or null when blank OR not locally
 * valid. The backend re-validates authoritatively; this just avoids firing
 * requests we already know it will reject.
 */
export function getFilterExpr(): string | null {
  const t = filterExpr.value.trim();
  if (!t) return null;
  if (validateExpr(t, exprKindOf) !== null) return null;
  return t;
}

/**
 * Collapse the filter atoms into the wire `FilterGroup`, or null when no row
 * carries both a column and a value. Values are typed off the column's declared
 * type - a numeric column coerces a finite `Number(val)`, but a VARCHAR value
 * like "001" stays a string (else the server stringifies it back to "1" and the
 * filter matches nothing). Derived columns count as numeric once §10.4 lands.
 */
export interface FilterOptions {
  /**
   * Leave slicer rows out entirely - for Line and Scatter requests, which fetch
   * the slicer columns instead (`slice_columns`) and apply the ranges locally.
   * Everywhere else a narrowed slicer is sent as a `>=` / `<=` pair, so charts
   * the server aggregates, Export and the row count all honour it.
   */
  omitSlicers?: boolean;
}

/** A slicer end as a filter value: epoch seconds become a UTC datetime for temporal columns. */
function slicerValue(column: string, v: number): string | number {
  const col = allColumns.value.find((c) => c.name === column);
  if (col && isTemporalType(col.type)) {
    return new Date(v * 1000).toISOString().slice(0, 19).replace('T', ' ');
  }
  return v;
}

export function getFilters(src?: ShapingSource, opts: FilterOptions = {}): FilterGroup | null {
  const all = src?.filterRows ?? filters.value;
  const rows = all.filter((r) => !r.slicer);
  const slicerConditions: FilterCondition[] = [];
  if (!opts.omitSlicers) {
    for (const r of all) {
      if (!r.slicer || !r.column) continue;
      if (r.slicer.min != null) slicerConditions.push({ column: r.column, op: '>=', value: slicerValue(r.column, r.slicer.min) });
      if (r.slicer.max != null) slicerConditions.push({ column: r.column, op: '<=', value: slicerValue(r.column, r.slicer.max) });
    }
  }
  // A dashboard slicer selection is one `in` per column. Unlike a range slicer
  // it is applied on every chart, Line and Scatter included.
  const inConditions: FilterCondition[] = [];
  for (const s of src?.slicers ?? []) {
    if (!s.column) continue;
    if (s.values?.length) {
      inConditions.push({ column: s.column, op: 'in', value: '', values: [...s.values] });
      continue;
    }
    const edge = (v: number): string | number =>
      s.temporal ? new Date(v * 1000).toISOString().slice(0, 19).replace('T', ' ') : v;
    if (s.min != null) inConditions.push({ column: s.column, op: '>=', value: edge(s.min) });
    if (s.max != null) inConditions.push({ column: s.column, op: '<=', value: edge(s.max) });
  }
  if (rows.length === 0 && slicerConditions.length === 0 && inConditions.length === 0) return null;

  const numericCols = new Set(
    allColumns.value.filter((c) => isNumericType(c.type)).map((c) => c.name),
  );
  // Derived signals are numeric pseudo-columns (§10.4) - coerce their values too.
  for (const d of src?.derived ?? derivedSignals.value) numericCols.add(d.name);

  const conditions = [];
  for (const row of rows) {
    const col = row.column;
    if (!col) continue;

    // Column-vs-column: RHS is another column, not a literal. The value input
    // is ignored in this mode.
    if (row.value_column) {
      conditions.push({ column: col, op: row.op, value: '', value_column: row.value_column });
      continue;
    }

    const raw = row.value;
    const val = typeof raw === 'string' ? raw : String(raw);
    if (val === '') continue;

    let value: string | number = val;
    if (numericCols.has(col)) {
      const n = Number(val);
      value = Number.isFinite(n) ? n : val;
    }
    conditions.push({ column: col, op: row.op, value });
  }
  // A slicer narrows what is shown, whatever the other rows' logic - under OR
  // it would widen instead. So with OR rows present, slicers are applied only
  // where they can be ANDed: locally on Line and Scatter.
  const logic = src?.logic ?? filterLogic.value;
  if (logic === 'AND' || conditions.length === 0) conditions.push(...slicerConditions, ...inConditions);
  if (conditions.length === 0) return null;
  return {
    logic: conditions.length === slicerConditions.length + inConditions.length ? 'AND' : logic,
    conditions,
  };
}

/**
 * The active filter payload, honoring the Builder/Expr switch: in `'expr'` mode
 * only the expression is sent, in `'builder'` mode only the structured rows.
 * Returns a fragment to spread onto a request body - empty when the active mode
 * has nothing to contribute. The single funnel every consumer (estimate,
 * export, plot) goes through, so the XOR rule lives in exactly one place.
 */
export function getActiveFilters(src?: ShapingSource, opts: FilterOptions = {}): {
  filters?: FilterGroup;
  filter_expr?: string;
} {
  // `'and'` (the app's own mode) sends both; the legacy `'builder'` / `'expr'`
  // a saved tile may carry send only their half, as they always did.
  const mode = src?.mode ?? filterMode.value;
  const out: { filters?: FilterGroup; filter_expr?: string } = {};
  if (mode !== 'expr') {
    const fg = getFilters(src, opts);
    if (fg) out.filters = fg;
  }
  if (mode !== 'builder') {
    // A supplied expression skips the local validator: it is validated against
    // the atoms' schema, which is the wrong schema for a dashboard tile carrying
    // its own. The backend validates authoritatively either way.
    const fe = src ? (src.expr ?? '').trim() || null : getFilterExpr();
    if (fe) out.filter_expr = fe;
  }
  return out;
}

/**
 * The active outlier spec to send, or an empty fragment when filtering is off
 * (method `none`) or no target column is picked. Only the params relevant to the
 * chosen method are attached. The single funnel both export and plot go through.
 */
export function getActiveOutliers(src?: ShapingSource): { outliers?: OutlierSpecReq } {
  const o = src?.outliers ?? outliers.value;
  if (o.method === 'none' || o.columns.length === 0) return {};
  const spec: OutlierSpecReq = {
    method: o.method,
    columns: [...o.columns],
    action: o.action,
  };
  if (o.method === 'iqr') spec.k = o.k;
  if (o.method === 'zscore' || o.method === 'mad') spec.n_sigma = o.nSigma;
  if (o.method === 'hampel') {
    spec.n_sigma = o.nSigma;
    spec.window = o.window;
  }
  if (o.method === 'percentile') {
    spec.p_low = o.pLow;
    spec.p_high = o.pHigh;
  }
  return { outliers: spec };
}

/**
 * The derived-signal definitions to send, or an empty fragment when none are
 * defined. The single funnel both plot and export go through, so the wire shape
 * lives in one place. Sent on export too (not just plots) so filter/outlier
 * targets that reference a derived signal resolve server-side - export never
 * *projects* derived columns, but it can filter rows by them.
 */
export function getActiveDerived(src?: ShapingSource): { derived?: DerivedSignalReq[] } {
  // A row with no formula yet (just added) has nothing to compute; the backend
  // would reject it, so it waits.
  const list = (src?.derived ?? derivedSignals.value).filter((d) => d.expr == null || d.expr.trim() !== '');
  if (list.length === 0) return {};
  const derived: DerivedSignalReq[] = list.map((d) => {
    const req: DerivedSignalReq = {
      name: d.name,
      op: d.op,
      operand_a: d.operand_a || null,
      operand_a_const: d.operand_a_const == null ? null : Number(d.operand_a_const),
      operand_b_col: d.operand_b_col || null,
      operand_b_const: d.operand_b_const == null ? null : Number(d.operand_b_const),
    };
    if (d.expr && d.expr.trim()) req.expr = d.expr;
    return req;
  });
  return { derived };
}

/**
 * The three shaping funnels as one fragment, ready to spread onto a request
 * body. With no argument it reads the shared atoms - what Explore sends; with a
 * `ShapingSource` it sends that instead, which is how a dashboard tile ships the
 * filters it was saved with.
 */
export type ShapingFragment = {
  filters?: FilterGroup;
  filter_expr?: string;
  outliers?: OutlierSpecReq;
  derived?: DerivedSignalReq[];
};

export function buildShaping(src?: ShapingSource, opts: FilterOptions = {}): ShapingFragment {
  return { ...getActiveFilters(src, opts), ...getActiveOutliers(src), ...getActiveDerived(src) };
}

/** Resample frequency string (e.g. `"3min"`), or null when blank / < 1. */
export function getFrequency(): string | null {
  const val = frequencyValue.value.trim();
  if (!val) return null;
  const num = parseInt(val, 10);
  if (!num || num < 1) return null;
  return num + frequencyUnit.value;
}

/**
 * Build the `/estimate` · `/preview` · `/export` request from the current atoms,
 * or null when ship / dates aren't ready. `columns` is sent only as an explicit
 * array when a strict subset is selected (0 < n < all) - null means "everything",
 * matching the blob. Derived signals are sent so filter/outlier targets that
 * reference them resolve server-side (they are not projected as output columns).
 */
export function buildExportRequest(): ExportRequest | null {
  const ship = selectedShip.value;
  const start = startDate.value;
  const end = endDate.value;
  if (!ship || !start || !end) return null;

  const cols = [...selectedCols.value];
  const total = allColumns.value.length;

  // `exactOptionalPropertyTypes` is on, so only attach optionals when present
  // (omitting a key ≠ setting it to undefined).
  const req: ExportRequest = {
    ...sourceField(activeDataSource.value),
    ship_id: ship,
    start_date: start,
    end_date: end,
    format: exportFormat.value,
  };
  if (cols.length > 0 && cols.length < total) req.columns = cols;
  Object.assign(req, getActiveFilters());
  Object.assign(req, getActiveOutliers());
  Object.assign(req, getActiveDerived());
  const frequency = getFrequency();
  if (frequency) req.frequency = frequency;
  if (fillMethod.value !== 'none') req.fill_method = fillMethod.value;
  return req;
}
